import { Grid, Stack, Typography, Chip, Card, styled, Button, Tooltip } from "@mui/material";
import React, { useContext, useMemo } from "react";

import CommentOutlinedIcon from "@mui/icons-material/CommentOutlined";
import PersonIcon from "@mui/icons-material/Person";
import PanToolIcon from "@mui/icons-material/PanTool";
import { DbOrderManualTaskPlanningInfos, TaskTag } from "../../../Core/Api/Types/db-order-manual-task-planning-infos";
import AvatarProfilePicture from "../../Misc/AvatarProfilePicture/avatar-profile-picture";
import { planningUserTaskService } from "../../../Core/Api/services";
import useCustomSnackbar from "../../../Utils/Hooks/useCustomSnackbar";
import { ModalContext } from "../../Modals";
import ModalEditTask, { ModalEditTaskElementProps } from "../../Modals/ModalEditTask/modal-edit-task";
import { DbUserBasicInfos } from "../../../Core/Api/Types/db-user-basic-infos";
import { DbOrderManualTaskPlanningResourceInfos } from "../../../Core/Api/Types/db-order-manual-task-planning-resource-infos";
import moment from "moment";
import GlobalConst from "../../../Core/Utils/Constants/globals";
import { useTranslation } from "react-i18next";
export interface KanbanTaskCardProps {
  task: DbOrderManualTaskPlanningInfos;
  users: DbUserBasicInfos[];
  resources: DbOrderManualTaskPlanningResourceInfos[];
  onClick?: (taskId: number) => void;
}

const KanbanTaskCard: React.FC<KanbanTaskCardProps> = ({ task, users, resources, onClick }) => {
  const { t } = useTranslation();
  const firstName = task.userName?.split(" ")[0];
  const lastName = task.userName?.split(" ")[1];
  const enqueueCustomSnackbar = useCustomSnackbar();
  const modalContext = useContext(ModalContext);
  const { openModal } = { ...modalContext };

  const claim = () => {
    planningUserTaskService.ClaimTaskById(task.id).then((response) => {
      enqueueCustomSnackbar(t("alert:success.taskClaimed", { taskName: task.name }), "success");
    });
  };

  const assign = () => {
    openModal &&
      openModal<ModalEditTaskElementProps>(ModalEditTask, {
        task,
        users,
        resources,
      });
  };

  const eventToDisplay = useMemo(
    () => task.events?.find((x) => x.assignInfos?.date && x.assignInfos.date >= new Date()) || task.events?.at(-1),
    [task.events]
  );

  return (
    <StyledCard variant="outlined" sx={{ p: 1, mt: 0.5, mb: 0.5 }}>
      <Grid container spacing={1} alignItems="center">
        <Grid item container>
          <Grid item xs={2}>
            <AvatarProfilePicture userId={task.userId ?? undefined} userFirstName={firstName} userLastName={lastName} />
          </Grid>
          <Grid item xs={7}>
            <Stack>
              <Typography component="h4" fontWeight={500}>
                {task.taskName}
              </Typography>
              <Typography variant="body2" color="text.tier">
                {task.titleName}
                {task.tags && task.tags.filter((x) => x.tag === "EpNb").length > 0
                  ? ` - EP. ${task.tags?.filter((x) => x.tag === "EpNb")[0].value}`
                  : ""}
              </Typography>
            </Stack>
          </Grid>
          <Grid item xs={3}>
            <Stack spacing={1}>
              <Tooltip
                title={
                  !task.events?.some((e) => e.assignInfos)
                    ? t("tooltip:title.assignTask")
                    : t("tooltip:title.assignedTo", { name: task.events.flatMap((e) => e.assignInfos?.userName) })
                }
              >
                <Button
                  size="small"
                  sx={{ p: 0.5 }}
                  color={task.events?.some((e) => e.assignInfos) ? "success" : "inherit"}
                  onClick={() => assign()}
                >
                  <PersonIcon />
                </Button>
              </Tooltip>
              <Tooltip
                title={
                  !task.events?.some((e) => e.claimInfos)
                    ? t("tooltip:title.claimTask")
                    : t("tooltip:title.claimedBy", { name: task.events?.flatMap((e) => e.claimInfos?.userName) })
                }
              >
                <Button
                  size="small"
                  sx={{ p: 0.5 }}
                  color={task.events?.some((e) => e.claimInfos) ? "success" : "inherit"}
                  onClick={() => claim()}
                >
                  <PanToolIcon />
                </Button>
              </Tooltip>
              <Chip
                color={task.activitiesCount > 0 ? "info" : "default"}
                label={task.activitiesCount}
                icon={<CommentOutlinedIcon />}
                onClick={() => {
                  if (onClick) onClick(task.id);
                }}
              />
            </Stack>
          </Grid>
        </Grid>
        <Grid item container spacing={1}>
          <Grid item>
            <Typography variant="body2" color="text.tier" fontWeight={500}>
              {t("field:label.assignedTo")}
            </Typography>
          </Grid>
          <Grid item>
            <Typography variant="body2" color="text.primary" fontWeight={500}>
              {eventToDisplay?.assignInfos?.userName ?? "-"} -{" "}
              {moment(eventToDisplay?.assignInfos?.date?.toString()).format(GlobalConst.MOMENT_DATE_FORMAT_FULL)}
            </Typography>
          </Grid>
        </Grid>
        <Grid item container spacing={1}>
          <Grid item>
            <Typography variant="body2" color="text.tier" fontWeight={500}>
              {t("common:notes")}
            </Typography>
          </Grid>
          <Grid item>
            <Typography variant="body2" color="text.primary" fontWeight={500}>
              {eventToDisplay?.comment ?? "-"}
            </Typography>
          </Grid>
        </Grid>
        <Grid item container spacing={1}>
          <GridItem title={t("menu:item.erpWorkOrder")} value={task.taskErpCode ?? "-"} />
          <GridItem
            title={t("common:version")}
            value={task.tags?.find((x) => x.tag === TaskTag.SubtitleVersion)?.value || "-"}
          />
          <GridItem title={t("common:duration")} value={task.mpDuration ? `${task.mpDuration} min` : "Not defined"} />
        </Grid>
        <Grid item container spacing={1}>
          <GridItem title={t("common:customer")} value={task.customerName ?? "-"} />
          <GridItem
            title={t("common:dueDate")}
            value={
              task.orderDueDate ? moment(task.orderDueDate.toString()).format(GlobalConst.MOMENT_DATE_FORMAT_FULL) : "-"
            }
          />
          <GridItem title={t("common:status")} value={task.status?.toString() ?? "-"} />
        </Grid>
      </Grid>
    </StyledCard>
  );
};

export default KanbanTaskCard;

const GridItem = (props: { title: string; value: string }) => {
  return (
    <Grid item xs={true}>
      <Typography variant="body2" color="text.tier" fontWeight={500}>
        {props.title}
      </Typography>
      <Typography variant="body2" color="text.primary" fontWeight={500}>
        {props.value}
      </Typography>
    </Grid>
  );
};

const StyledCard = styled(Card)(({ theme }) => ({
  transition: "all .3s",
  background: theme.palette.backgroundAlpha.main,
  "&:hover": {
    background: theme.palette.backgroundAlpha.hover,
  },
}));
